import React, { Fragment, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBell } from "@fortawesome/free-solid-svg-icons";
import { 
  Dropdown, 
  DropdownToggle, 
  DropdownMenu, 
  Badge,
  Button,
  Spinner
} from 'reactstrap';
import { useAuth } from "../../../auth/AuthContext";
import { useNotifications } from "../../../contexts/NotificationContext";

const UserBox = () => {
  const { user } = useAuth();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const { 
    notifications, 
    unreadCount, 
    isConnected, 
    markAsRead, 
    markAllAsRead,
    refreshNotifications 
  } = useNotifications();

  // Debug notifications
  console.log('UserBox notifications:', notifications);

  const username = user?.username || '';
  const toggle = () => setDropdownOpen(prevState => !prevState);

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'success': return 'fas fa-check-circle text-success';
      case 'warning': return 'fas fa-exclamation-triangle text-warning';
      case 'error': return 'fas fa-times-circle text-danger';
      default: return 'fas fa-info-circle text-info';
    }
  };

  const formatTimeAgo = (dateString) => {
    const now = new Date();
    let notificationDate;
    
    // Backend sends UTC timestamps without timezone info
    // We need to treat them as UTC and let the browser convert to local time
    if (dateString.includes('Z') || dateString.includes('+') || dateString.includes('-')) {
      // Already has timezone info
      notificationDate = new Date(dateString);
    } else {
      // Backend sends UTC without timezone info, append 'Z' to indicate UTC
      notificationDate = new Date(dateString + 'Z');
    }
    
    // Handle invalid dates
    if (isNaN(notificationDate.getTime())) {
      console.error('Invalid date string:', dateString);
      return 'Unknown time';
    }
    
    const diffInSeconds = Math.floor((now - notificationDate) / 1000);
    
    // Debug logging
    console.log('formatTimeAgo debug:', {
      dateString,
      notificationDate: notificationDate.toISOString(),
      notificationDateLocal: notificationDate.toLocaleString(),
      now: now.toISOString(),
      nowLocal: now.toLocaleString(),
      diffInSeconds,
      diffInMinutes: Math.floor(diffInSeconds / 60),
      diffInHours: Math.floor(diffInSeconds / 3600)
    });
    
    // Handle negative time differences (future dates)
    if (diffInSeconds < 0) {
      const absDiff = Math.abs(diffInSeconds);
      if (absDiff < 60) return 'Just now';
      if (absDiff < 3600) return `${Math.floor(absDiff / 60)}m ago`;
      if (absDiff < 86400) return `${Math.floor(absDiff / 3600)}h ago`;
      return `${Math.floor(absDiff / 86400)}d ago`;
    }
    
    // Handle positive time differences (past dates)
    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    return `${Math.floor(diffInSeconds / 86400)}d ago`;
  };

  const handleNotificationClick = (notification, event) => {
    // Prevent dropdown from closing
    event.stopPropagation();
    
    if (!notification.is_read) {
      markAsRead(notification.id);
    }
  };

  return (
    <Fragment>
      <div className="header-btn-lg pe-0">
        <div className="widget-content p-0">
          <div className="widget-content-wrapper">
            <div className="widget-content-left">
              <div className="d-flex align-items-center">
                <div className="me-2" style={{ padding: '8px', overflow: 'visible', height: '50px', display: 'flex', alignItems: 'center' }}>
                  <Dropdown isOpen={dropdownOpen} toggle={toggle}>
                    <DropdownToggle caret className="btn btn-link p-0 iconClass" style={{ 
                      border: 'none', 
                      background: 'none',
                      overflow: 'visible',
                      padding: '0',
                      height: '40px',
                      width: '40px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <FontAwesomeIcon 
                        icon={faBell} 
                        size="2x" 
                        style={{ color: 'var(--cryrDarkBlue)' }}
                      />
                      {unreadCount > 0 && (
                        <span className="badge badge-danger" style={{ 
                          fontSize: '0.75rem', 
                          backgroundColor: '#dc3545', 
                          color: 'white',
                          minWidth: '20px',
                          height: '20px',
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          padding: '0'
                        }}>
                          {unreadCount > 99 ? '99+' : unreadCount}
                        </span>
                      )}
                      {!isConnected && (
                        <i className="fas fa-circle text-warning position-absolute" 
                           style={{ fontSize: '0.5rem', top: '2px', right: '2px' }}
                           title="Connection lost"></i>
                      )}
                    </DropdownToggle>
                    
                    <DropdownMenu end className="notification-dropdown">
                      {/* Header */}
                      <div className="d-flex justify-content-between align-items-center p-3 border-bottom">
                        <h6 className="mb-0">
                          <i className="fas fa-bell me-2"></i>
                          Notifications
                          {!isConnected && (
                            <small className="text-warning ms-2">
                              <i className="fas fa-exclamation-triangle"></i> Offline
                            </small>
                          )}
                        </h6>
                        <div>
                          {unreadCount > 0 && (
                            <Button 
                              size="sm" 
                              color="link" 
                              onClick={(event) => {
                                event.stopPropagation();
                                markAllAsRead();
                              }}
                              className="p-0 me-2"
                              title="Mark all as read"
                            >
                              <i className="fas fa-check-double"></i>
                            </Button>
                          )}
                          <Button 
                            size="sm" 
                            color="link" 
                            onClick={(event) => {
                              event.stopPropagation();
                              refreshNotifications();
                            }}
                            className="p-0"
                            title="Refresh"
                          >
                            <i className="fas fa-sync-alt"></i>
                          </Button>
                        </div>
                      </div>
                      
                      {/* Notifications List */}
                      {notifications.length === 0 ? (
                        <div className="p-4 text-center text-muted">
                          <i className="fas fa-bell-slash fa-2x mb-2"></i>
                          <div>No notifications</div>
                        </div>
                      ) : (
                        notifications.slice(0, 10).map(notification => (
                          <div 
                            key={notification.id}
                            className={`p-3 border-bottom ${!notification.is_read ? 'bg-light' : ''}`}
                            onClick={(event) => handleNotificationClick(notification, event)}
                            style={{ 
                              cursor: 'pointer',
                              transition: 'background-color 0.2s ease'
                            }}
                            onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                            onMouseLeave={(e) => e.target.style.backgroundColor = notification.is_read ? 'transparent' : '#f8f9fa'}
                          >
                            <div className="d-flex align-items-start">
                              <div className="me-3 mt-1">
                                <i className={`${getNotificationIcon(notification.type)}`}></i>
                              </div>
                              <div className="flex-grow-1">
                                <div className="fw-bold mb-1">{notification.title}</div>
                                <div className="text-muted small mb-2">{notification.message}</div>
                                <div className="d-flex justify-content-between align-items-center">
                                  <small className="text-muted">
                                    {formatTimeAgo(notification.created_at)}
                                  </small>
                                  {notification.category && (
                                    <Badge color="secondary" className="small">
                                      {notification.category}
                                    </Badge>
                                  )}
                                </div>
                              </div>
                              {!notification.is_read && (
                                <div className="bg-primary rounded-circle ms-2" 
                                     style={{ width: '8px', height: '8px', marginTop: '8px' }}></div>
                              )}
                            </div>
                          </div>
                        ))
                      )}
                      
                      {/* Footer */}
                      {notifications.length > 10 && (
                        <div className="p-2 text-center border-top">
                          <small className="text-muted">
                            Showing 10 of {notifications.length} notifications
                          </small>
                        </div>
                      )}
                    </DropdownMenu>
                  </Dropdown>
                </div>
                <div className="header-user-info">
                  <div className="widget-heading" style={{ color: 'var(--cryrDarkBlue)', fontWeight: 'normal' }}>
                    Welcome, {username || 'User'}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default UserBox;
